<script setup lang="ts">
import { formatYearsOfExperience } from '../format';
import { formatReviewCount } from '@/features/reviews';
import { useAuthModalStore } from '@glow/shared/features/auth';
import { makeFragmentData } from '@/gql';
import { contactFieldsFragment } from '@/features/users';
import PartnerContactsModal from './PartnerContactsModal.vue';
import type { RouteLocationRaw } from 'vue-router';
import type { ContactFieldsFragment } from '@/gql/graphql';
import { HeartOutlinedIcon, HeartIcon } from '@glow/shared/components/icons';

export type PartnerCardPartner = {
  id: number;
  name: string;
  avatarUrl?: string;
  about: string;
  rating: number;
  isLiked: boolean;
  yearsOfExperience?: number;
  reviewCount: number;
  link: RouteLocationRaw;
} & ContactFieldsFragment;

const props = withDefaults(
  defineProps<{
    locationIndex?: number;
    partner: PartnerCardPartner;
    withMessageButton?: boolean;
    isMessageLoading?: boolean;
  }>(),
  {
    locationIndex: undefined,
    withMessageButton: true,
    isMessageLoading: false,
  }
);
const emit = defineEmits<{
  (e: 'message'): void;
  (e: 'updateLikedState', newState: boolean): void;
  (e: 'update:locationIndex', locationIndex?: number): void;
}>();

const { ensureLoggedIn } = useAuthModalStore();
const isContactModalVisible = ref(false);
const isLightboxVisible = ref(false);

const reviewCountString = computed(() =>
  formatReviewCount(props.partner.reviewCount)
);
const experienceString = computed(() =>
  formatYearsOfExperience(props.partner.yearsOfExperience ?? 0)
);
const contactFields = computed(() =>
  makeFragmentData(props.partner, contactFieldsFragment)
);

const {
  value: about,
  isCollapsed: isAboutCollapsed,
  shouldCollapse: shouldAboutCollapse,
  toggleCollapseState: toggleAboutCollapseState,
} = useCollapsibleArray(
  computed(() => props.partner.about ?? ''),
  300,
  true
);

const isLiked = computed({
  get: () => props.partner.isLiked,
  set: (value) => {
    ensureLoggedIn(() => {
      emit('updateLikedState', value);
    });
  },
});
function tryOpenAvatarLightbox() {
  if (!props.partner.avatarUrl) return;
  isLightboxVisible.value = true;
}
</script>

<template>
  <div class="details">
    <div class="details__heading">
      <AppCheckbox
        v-model="isLiked"
        class="d-md-none details__like"
        variant="like"
      />
      <AppAvatar
        size="lg"
        :name="props.partner.name"
        :src="props.partner.avatarUrl"
        :clickable="!!props.partner.avatarUrl"
        @click="tryOpenAvatarLightbox"
      />
      <div
        class="d-flex flex-column align-items-center align-items-sm-start gap-2"
      >
        <div v-if="props.partner.yearsOfExperience" class="details__experience">
          {{ experienceString }}
        </div>
        <h1 class="details__name">{{ props.partner.name }}</h1>
        <div class="text-center d-sm-none">
          {{ about }}
          <AppButton
            v-if="shouldAboutCollapse"
            variant="link"
            @click="toggleAboutCollapseState"
          >
            {{ isAboutCollapsed ? 'Развернуть' : 'Свернуть' }}
          </AppButton>
        </div>
        <div class="d-flex align-items-center gap-2">
          <AppRating
            :model-value="props.partner.rating"
            class="details__rating"
          />
          <div class="details__review-count">({{ reviewCountString }})</div>
        </div>
        <div class="flex-1"></div>
        <div class="d-flex gap-2">
          <AppButton
            border-radius="lg"
            class="font-bold d-none d-sm-block"
            variant="outlined"
            :icon-start="!isLiked ? HeartOutlinedIcon : HeartIcon"
            @click="isLiked = !isLiked"
          >
            {{ !isLiked ? 'В избранное' : 'В избранном' }}
          </AppButton>
          <AppButton
            v-if="props.withMessageButton"
            border-radius="lg"
            class="font-bold"
            :loading="props.isMessageLoading"
            @click="emit('message')"
          >
            Сообщение
          </AppButton>
          <AppButton
            class="font-bold"
            variant="outlined"
            border-radius="lg"
            @click="isContactModalVisible = true"
          >
            Контакты
          </AppButton>
        </div>
      </div>
    </div>
    <div v-if="props.partner.about" class="d-none d-sm-flex flex-column gap-6">
      <AppSeparator />
      <h2 class="m-0">О себе</h2>
      <div class="text-black-secondary">
        {{ about }}
        <AppButton
          v-if="shouldAboutCollapse"
          variant="link"
          @click="toggleAboutCollapseState"
        >
          {{ isAboutCollapsed ? 'Развернуть' : 'Свернуть' }}
        </AppButton>
      </div>
    </div>
  </div>

  <AppLightbox
    v-model="isLightboxVisible"
    :image-urls="props.partner.avatarUrl ? [props.partner.avatarUrl] : []"
  />

  <PartnerContactsModal
    v-model="isContactModalVisible"
    :fields="contactFields"
  />
</template>

<style lang="scss" scoped>
.details {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: stretch-size(375, 1920, 18, 28);
  width: 100%;

  &__like {
    position: absolute;
    right: 0;
    top: 0;
  }

  &__heading {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: stretch-size(375, 1920, 12, 16);

    @include media-breakpoint-down(sm) {
      flex-direction: column;
      align-items: center;
    }
  }

  &__experience {
    margin-bottom: 4px;
    font-weight: 400;
    font-size: stretch-size(375, 1920, 13, 15);
    line-height: stretch-size(375, 1920, 15, 20);
    color: var(--app-color-black-secondary);
  }

  &__name {
    margin-bottom: 4px;
    color: var(--app-color-black);

    @include media-breakpoint-down(sm) {
      font-size: 16px;
    }
  }

  &__review-count {
    font-size: stretch-size(375, 1920, 13, 15);
    line-height: stretch-size(375, 1920, 15, 20);
    color: var(--app-color-primary);
  }
}
</style>
